import type { z } from "zod"

import type { DomainEventType } from "../taxonomy.js"
import {
  evaluateBillsPolicy,
  type BillsAgentFacts,
} from "./bills-policy.js"
import {
  BillsAgentConfigSchema,
  StatementsAgentConfigSchema,
  defaultBillsAgentConfig,
  defaultStatementsAgentConfig,
} from "./schemas.js"
import {
  evaluateStatementsPolicy,
  type StatementsAgentFacts,
} from "./statements-policy.js"
import { agentKinds } from "./vocabulary.js"
import type {
  AgentKind,
  BillsVerdict,
  StatementsVerdict,
} from "./vocabulary.js"

/**
 * The agent registry, mirroring the document type registry: each kind
 * declares the events it consumes, its config schema, its pure evaluator and
 * its copy. The engine, the tables, the routes and the settings card are
 * generic over this, so a third agent is a content change here rather than an
 * architecture change anywhere else.
 *
 * The map is a discriminated union keyed by kind, not one definition type
 * repeated: a kind's facts and config are its own, and only the code that
 * knows the kind may reach for its evaluator.
 */

export type AgentDefinition<
  Kind extends AgentKind,
  ConfigSchema extends z.ZodTypeAny,
  Facts,
  Verdict,
> = {
  readonly kind: Kind
  /** Sentence case, house voice: what the settings card calls it. */
  readonly name: string
  readonly description: string
  /** The outbox events whose `agent` rows this kind evaluates. */
  readonly consumedEvents: readonly DomainEventType[]
  readonly configSchema: ConfigSchema
  readonly defaultConfig: z.infer<ConfigSchema>
  readonly evaluate: (facts: Facts, config: z.infer<ConfigSchema>) => Verdict
}

export type BillsAgentDefinition = AgentDefinition<
  "bills",
  typeof BillsAgentConfigSchema,
  BillsAgentFacts,
  BillsVerdict
>

export type StatementsAgentDefinition = AgentDefinition<
  "statements",
  typeof StatementsAgentConfigSchema,
  StatementsAgentFacts,
  StatementsVerdict
>

export const billsAgentDefinition: BillsAgentDefinition = {
  kind: "bills",
  name: "Bills agent",
  description:
    "The agent applies your policy to every bill that arrives. It approves a bill when every check passes, and holds the rest for you with the reasons.",
  consumedEvents: ["document_processed"],
  configSchema: BillsAgentConfigSchema,
  defaultConfig: defaultBillsAgentConfig,
  evaluate: evaluateBillsPolicy,
}

export const statementsAgentDefinition: StatementsAgentDefinition = {
  kind: "statements",
  name: "Statements agent",
  description:
    "The agent applies your policy to every bank statement that arrives. It approves a statement when every check passes, and holds the rest for you with the reasons.",
  consumedEvents: ["document_processed"],
  configSchema: StatementsAgentConfigSchema,
  defaultConfig: defaultStatementsAgentConfig,
  evaluate: evaluateStatementsPolicy,
}

/**
 * Every kind, in the order the settings surface lists them. The map type is
 * what makes a kind without a registry entry a compile error, and what keeps
 * one kind's config schema from being handed another kind's facts.
 */
export type AgentDefinitionMap = {
  readonly bills: BillsAgentDefinition
  readonly statements: StatementsAgentDefinition
}

export const agentRegistry = {
  bills: billsAgentDefinition,
  statements: statementsAgentDefinition,
} as const satisfies AgentDefinitionMap

/** Any kind's definition, for the code that reads the registry generically. */
export type AnyAgentDefinition = AgentDefinitionMap[AgentKind]

export const agentDefinitionFor = <Kind extends AgentKind>(
  kind: Kind
): AgentDefinitionMap[Kind] => agentRegistry[kind]

/**
 * Every event any kind consumes, deduplicated. The outbox writes an `agent`
 * row for exactly these, the way it writes a `workflow` row for exactly the
 * selectable triggers: a new event reaches every consumer or none, and there
 * is one place to read to find out which.
 */
export const agentConsumedEvents: readonly DomainEventType[] = [
  ...new Set(
    Object.values<AnyAgentDefinition>(agentRegistry).flatMap(
      (definition) => definition.consumedEvents
    )
  ),
]

export const isAgentConsumedEvent = (eventType: DomainEventType): boolean =>
  agentConsumedEvents.includes(eventType)

/** Which kinds evaluate this event, in registry order. */
export const agentKindsForEvent = (
  eventType: DomainEventType
): readonly AgentKind[] =>
  agentKinds.filter((kind) =>
    agentRegistry[kind].consumedEvents.includes(eventType)
  )

/**
 * The once-per-event promise, written down. The event alone would let one
 * evaluation cover a batch; the document alone would drop a second event
 * about the same document. Unique on kind plus this, so redelivery is a
 * no-op.
 */
export const agentActionIdempotencyKey = (input: {
  readonly eventId: string | null
  readonly documentId: string
  /** Falls back to the outbox row when the payload named no domain event. */
  readonly outboxMessageId: string
}): string =>
  `${input.eventId ?? `outbox:${input.outboxMessageId}`}:${input.documentId}`
